import { ChangeEvent, FC, useCallback } from 'react';
import { useField } from 'react-final-form';

import { NumberField, NumberFieldProps } from '../NumberField/NumberField';

export interface FormNumberFieldProps extends Omit<NumberFieldProps, 'value'> {
  name: string;
  validate?: (value: string | number | undefined) => string;
}

export const FormNumberField: FC<FormNumberFieldProps> = ({
  name,
  positive,
  integer,
  required,
  onChange,
  min,
  max,
  validate: validateFromProps,
  ...rest
}) => {
  const validate = useCallback(
    (value: string | number | undefined) => {
      const v = value === undefined ? undefined : +value;

      if (required && v === undefined) return 'Заполните поле';
      let minValue = -Infinity;
      let maxValue = Infinity;
      if (min) minValue = +min;
      if (max) maxValue = +max;
      if (positive && minValue < 0) minValue = 0;

      if (v !== undefined && v < minValue) return `Не менее ${minValue}`;
      if (v !== undefined && v > maxValue) return `Не более ${maxValue}`;

      if (integer && v !== undefined && !Number.isInteger(v))
        return 'Не целое число';

      if (validateFromProps) return validateFromProps(value);

      return '';
    },
    [integer, max, min, positive, required, validateFromProps],
  );

  const { input, meta } = useField(name, { type: 'number', validate });
  const error = meta.touched ? meta.error : undefined;

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      input.onChange(event);
      onChange?.(event);
    },
    [input, onChange],
  );

  return (
    <NumberField
      {...rest}
      {...input}
      positive={positive}
      integer={integer}
      required={required}
      error={!!error}
      helperText={error}
      onChange={handleChange}
      min={min}
      max={max}
    />
  );
};
